import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { CardVideo } from '@/design-system/atoms/Cards/CardVideo';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Image } from '@/design-system/atoms/Image';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import window from '@/utils/window';

import styles from './ResourceHero.module.scss';

const ResourceHero = ({
    id,
    enableBreadcrumbs,
    breadcrumbs,
    media,
    image,
    video,
    minutesToRead,
    categoryTag,
    contentType,
    title,
    ...props
}) => {
    const mediaContainerRef = useRef();
    const articleTemplateElement = window.document.querySelector('head meta[name="template"]');
    const pageTemplate = articleTemplateElement?.getAttribute('content') || 'general-content';

    const [mediaHeight, setMediaHeight] = useState(0);
    const themeName = contentType === 'article' ? Theme.NAME.MEDIUM : Theme.NAME.CONDITIONS;
    const eyebrowContentType = ['patient stories', 'article'].includes(contentType)
        ? contentType
        : 'press release';
    useEffect(() => {
        if (!mediaContainerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            setMediaHeight(mediaContainerRef?.current?.clientHeight / 2);
        });
        resizeObserver.observe(mediaContainerRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);
    return (
        <div style={{ marginBottom: `${mediaHeight}px` }} {...props}>
            <Theme name={themeName} className={styles[`resource-hero`]}>
                <ContentContainer className={styles[`resource-hero__container`]}>
                    {enableBreadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
                        <Breadcrumb
                            items={breadcrumbs}
                            analytics={{
                                componentName: 'resource_hero',
                                componentTitle: title,
                            }}
                        />
                    )}
                    <ContentContainer
                        isContentWidth
                        className={styles[`resource-hero__content-container`]}
                    >
                        <div className={styles[`resource-hero__content`]}>
                            {contentType && (
                                <div className={styles[`resource-hero__eyebrow`]}>
                                    {(contentType === 'article' ||
                                        contentType === 'patient stories') &&
                                        categoryTag && (
                                            <>
                                                <Text
                                                    content={categoryTag + ' ·'}
                                                    variant={Text.VARIANT.E1}
                                                    tag={Text.TAG.SPAN}
                                                    className={
                                                        styles[
                                                            `resource-hero__eyebrow--${pageTemplate.replaceAll(
                                                                ' ',
                                                                '-',
                                                            )}`
                                                        ]
                                                    }
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                    <Text
                                        content={eyebrowContentType}
                                        variant={Text.VARIANT.E1}
                                        tag={Text.TAG.SPAN}
                                        className={
                                            styles[
                                                `resource-hero__eyebrow--${pageTemplate.replaceAll(
                                                    ' ',
                                                    '-',
                                                )}`
                                            ]
                                        }
                                    />
                                </div>
                            )}
                            {title && (
                                <Title
                                    content={title}
                                    variant={Title.VARIANT.H3}
                                    tag={Title.TAG.H1}
                                    className={styles[`resource-hero__title`]}
                                />
                            )}
                            {minutesToRead && (
                                <Text
                                    content={`${minutesToRead} min read`}
                                    variant={Text.VARIANT.E1}
                                    tag={Text.TAG.SPAN}
                                    className={styles[`resource-hero__minutes-to-read`]}
                                />
                            )}
                        </div>
                        <div
                            className={styles[`resource-hero__media`]}
                            ref={mediaContainerRef}
                            style={{
                                transform: `translateY(${mediaHeight}px)`,
                                marginTop: `-${mediaHeight}px`,
                            }}
                        >
                            {media === 'image' && image && <Image {...image} roundedCorners />}
                            {media === 'video' && video && (
                                <CardVideo
                                    {...video}
                                    className={styles[`resource-hero__video`]}
                                    analytics={{ componentName: 'video_card_player' }}
                                />
                            )}
                        </div>
                    </ContentContainer>
                </ContentContainer>
            </Theme>
        </div>
    );
};

ResourceHero.propTypes = {
    /**
     * Breadcrumbs to display.
     */
    breadcrumbs: Breadcrumb.propTypes.items,
    /**
     * Media type to display.
     */
    media: PropTypes.oneOf(['image', 'video']),
    /**
     * Image properties.
     */
    image: PropTypes.shape(Image.propTypes),
    /**
     * Video properties.
     */
    video: PropTypes.shape(CardVideo.propTypes),
    /**
     * Minutes to read.
     */
    minutesToRead: PropTypes.number,
    /**
     * Theme name.
     */
    theme: PropTypes.oneOf(['medium', 'conditions']),
    /**
     * Category tag for Article Content Type
     */
    categoryTag: PropTypes.string,
    /**
     * Content type.
     */
    contentType: PropTypes.oneOf(['article', 'press-release', 'patient stories']),
    /**
     * Title.
     */
    title: PropTypes.string,
};

export default ResourceHero;
